import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PackageDetailsFields from './PackageDetailsFields';
import SaveButton from '../../shared/SaveButton';
import CancelButton from '../../shared/CancelButton';
import { formatNumber } from '../../shared/numberFormat';
import { useShipmentWeightUnit, formatDimensions } from '../../shared/useShipmentWeightUnit';
import { formatQty, detailsDraft, detailsPayload, openPackageOf, holdsAnything, shippablePackages } from './packageShared';
import { glowClass } from './fulfilmentGuide';
import '../../shared/StyledInput.css';

const smallButton = { padding: '6px 12px', borderRadius: 6, border: '1px solid #1976d2', background: '#eaf2fb', color: '#1976d2', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const quietButton = { ...smallButton, border: '1px solid #bfc8d6', background: '#fff', color: '#333' };

// "31.0 × 22.5 × 10.2 · 2.5 kg" — whatever's been recorded.
function detailsLine(pkg, weightUnit) {
  return [
    formatDimensions(pkg),
    pkg.weight !== null && pkg.weight !== undefined ? `${formatNumber(pkg.weight, { maxDecimals: 3 })}${weightUnit ? ` ${weightUnit}` : ''}` : null,
  ].filter(Boolean).join(' · ');
}

function Goods({ goods }) {
  return goods.map((c) => (
    <div key={c.line_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
      <span style={{ fontWeight: 600 }}>{c.sku}</span>
      <span style={{ color: '#555', whiteSpace: 'nowrap' }}>{formatQty(c.qty)}{c.uom ? ` ${c.uom}` : ''}</span>
    </div>
  ));
}

// Pick Order's packages (issues #299, #313, vis_order_fulfilment.md
// "Packages"): the open package filling as items are Packed, closed ones,
// and packages inside others at any depth, each with its own goods. Shown
// once anything's been packed. Nothing here is required — a one-package
// order just packs and ships — it's here for when an order needs more than
// one: Close starts the next package on the next scan, New Package opens
// an empty one for loose goods and closed packages to go into, Into puts a
// closed package in the open one, Take Out lifts it back out, and any
// closed package not inside another can be reopened. Each package's type
// is picked from the Packaging list; its Dimensions and Weight can be
// recorded any time before Ship.
// A label prints when staff ask, from Label (onLabel opens the preview,
// where they choose the size): a Parcel label while the package is
// outermost, a Package label once it's inside another (issue #445). Any
// package with something in it has one. Packing and labels happen at a
// packing station only: elsewhere (`canLabel` false, and `canUpdate` false)
// the packages show without their buttons. Packing List (issues #296, #445)
// prints one list for every package ready to ship, on any device
// (onPackingList opens it); onChange gets the action that changed the
// order and, on Close, the package closed, so its label can open.
// onTakeOut switches Pack to taking goods out of the open package — kept on
// its card, beside what's in it. `closeInNowPanel`: Pack's Now panel
// already offers Close, so the open package's card doesn't repeat it.
// `highlightId` marks the
// package a scanned Package Label named; `guideTarget` is the button the
// fulfilment guide suggests next (fulfilmentGuide.js).
function PackagesPanel({ order, canUpdate, canLabel, onChange, onError, onLabel, onTakeOut, onPackingList, highlightId, guideTarget, closeInNowPanel = false }) {
  const weightUnit = useShipmentWeightUnit();
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(detailsDraft(null));
  const [busy, setBusy] = useState(false);
  const [packagings, setPackagings] = useState([]);
  // Says what a Reopen did to another package (see reopen below).
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(timer);
  }, [notice]);
  useEffect(() => {
    if (!canUpdate) return;
    axios.get('/api/packagings')
      .then((res) => setPackagings(res.data.data || []))
      .catch(() => setPackagings([]));
  }, [canUpdate]);
  const packages = order.packages || [];
  if (!packages.length) return null;

  const openPkg = openPackageOf(packages);

  const act = async (action, body = {}, pkg = null) => {
    setBusy(true);
    try {
      const res = await axios.post(`/api/fulfilment/${order.order_type}/${order.id}/packages/${action}`, body);
      onChange(res.data.data, action, pkg);
      return true;
    } catch (err) {
      onError(err.response?.data?.message || 'Could not update the package');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // One package is open at a time, so reopening one closes whichever was
  // open (an empty one is dropped) — said out loud, so it doesn't look
  // like it happened by itself.
  const reopen = async (pkg) => {
    const current = openPkg;
    if (!(await act('reopen', { package_id: pkg.id }))) return;
    if (!current) setNotice('');
    else if (!holdsAnything(current)) setNotice(`${pkg.name} reopened. ${current.name} was empty, so it's gone.`);
    else setNotice(`${pkg.name} reopened. ${current.name} is now closed.`);
  };

  // Picking a type fills in that Packaging's dimensions, same as Details.
  const pickType = (pkg, packagingId) => {
    const packaging = packagings.find((p) => String(p.id) === String(packagingId));
    const dims = packaging ? Object.fromEntries(['length', 'width', 'height'].filter((k) => packaging[k] != null).map((k) => [k, packaging[k]])) : {};
    act('details', { package_id: pkg.id, packaging_id: packaging ? packaging.id : null, ...dims });
  };

  const startEdit = (pkg) => { setEditingId(pkg.id); setDraft(detailsDraft(pkg)); };
  const saveEdit = async () => {
    if (await act('details', { package_id: editingId, ...detailsPayload(draft) })) setEditingId(null);
  };

  const editor = (pkg) => (editingId === pkg.id ? (
    <div style={{ marginTop: 10, padding: 10, background: '#f7f9fc', border: '1px solid #e3e7ee', borderRadius: 6 }}>
      <PackageDetailsFields value={draft} onChange={setDraft} />
      <div>
        <SaveButton type="button" onClick={saveEdit} loading={busy} />
        <CancelButton onClick={() => setEditingId(null)} disabled={busy} style={{ marginLeft: 8 }} />
      </div>
    </div>
  ) : null);

  // The type picker, while a package can still change. A package recorded
  // with Packaging since retired still shows it.
  const typeSelect = (pkg) => {
    const listed = packagings.some((p) => String(p.id) === String(pkg.packaging_id));
    return (
      <select
        className="styled-input"
        title="What this package is"
        value={pkg.packaging_id ? String(pkg.packaging_id) : ''}
        disabled={busy}
        onChange={(e) => pickType(pkg, e.target.value)}
        style={{ fontSize: 12, padding: '4px 6px', maxWidth: 170 }}
      >
        <option value="">No type</option>
        {pkg.packaging_id && !listed && <option value={pkg.packaging_id}>{pkg.packaging?.name || '(retired)'}</option>}
        {packagings.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    );
  };

  const highlighted = (pkg) => String(pkg.id) === String(highlightId);

  // One package, and the packages inside it beneath its own goods.
  // `parent` is the package it sits in (null when outermost).
  const renderPackage = (pkg, parent) => {
    const outermost = !parent;
    const details = detailsLine(pkg, weightUnit);
    const buttons = [];
    if (canUpdate) {
      if (pkg.open) {
        if (!closeInNowPanel) buttons.push(
          <button key="close" type="button" style={smallButton} className={glowClass(guideTarget, 'close-package')} disabled={busy || !holdsAnything(pkg)} onClick={() => act('close', { package_id: pkg.id }, pkg)}>
            Close {pkg.name}
          </button>
        );
        if ((pkg.goods || []).length && onTakeOut) {
          buttons.push(<button key="take-out" type="button" style={quietButton} disabled={busy} onClick={onTakeOut}>Take Out Items</button>);
        }
      } else if (outermost) {
        buttons.push(<button key="reopen" type="button" style={quietButton} disabled={busy} onClick={() => reopen(pkg)}>Reopen</button>);
        if (openPkg) {
          buttons.push(
            <button key="into" type="button" style={smallButton} disabled={busy} onClick={() => act('move', { package_id: pkg.id, into_id: openPkg.id })}>
              Into {openPkg.name}
            </button>
          );
        }
      } else if (parent.open) {
        buttons.push(<button key="out" type="button" style={quietButton} disabled={busy} onClick={() => act('move', { package_id: pkg.id, into_id: null })}>Take Out</button>);
      }
      buttons.push(<button key="details" type="button" style={quietButton} disabled={busy} onClick={() => startEdit(pkg)}>Details</button>);
    }
    if ((canUpdate || canLabel) && holdsAnything(pkg) && onLabel) {
      buttons.push(<button key="label" type="button" style={quietButton} onClick={() => onLabel(pkg.id)}>Print Label</button>);
    }
    return (
      <div
        key={pkg.id}
        style={{
          ...(outermost
            ? { background: '#fff', border: `1px solid ${pkg.open ? '#8fd19e' : '#e3e7ee'}`, borderRadius: 8, padding: 12 }
            : { borderTop: '1px solid #eceef1', paddingTop: 8, marginTop: 8, marginLeft: 10 }),
          ...(highlighted(pkg) ? { outline: '2px solid #1976d2', outlineOffset: 2, borderRadius: 8 } : {}),
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>
            {pkg.name}
            {pkg.open && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#1e7e34', textTransform: 'uppercase', letterSpacing: 0.3 }}>open</span>}
          </span>
          {canUpdate && typeSelect(pkg)}
        </div>
        {details && <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{details}</div>}
        <Goods goods={pkg.goods || []} />
        {!holdsAnything(pkg) && (
          <div style={{ fontSize: 12, color: '#888' }}>
            {pkg.open ? 'Empty. Scan items into it, or put closed packages in with “Into”.' : 'Empty'}
          </div>
        )}
        {(pkg.packages || []).map((inner) => renderPackage(inner, pkg))}
        {buttons.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>{buttons}</div>}
        {editor(pkg)}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Packages
      </div>
      {notice && (
        <div style={{ marginBottom: 8, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, background: '#eef4fb', color: '#1a4971' }}>{notice}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {packages.map((p) => renderPackage(p, null))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {canUpdate && (
          <button type="button" style={quietButton} className={glowClass(guideTarget, 'new-package')} disabled={busy} onClick={() => act('new')}>
            New Package
          </button>
        )}
        {onPackingList && shippablePackages(packages).length > 0 && (
          <button type="button" style={quietButton} onClick={onPackingList}>Packing List</button>
        )}
      </div>
    </div>
  );
}

export default PackagesPanel;
